// RGR16 정불구분(ETC5) 칸 설정 — GET /api/v1/return/inbounds/etc5-setting (moms-be ReturnInboundColumnSettingController).
// 1.0 RGR01 은 보는 사람의 화주 그리드 설정(MOMS_GRID_COLUMN_INFO)으로 이 칸의 헤더명·편집 여부·입력 방식을 정했다(LOGISPRD-16194).
import { useQuery, UseQueryResult } from '@tanstack/react-query'
import type { core } from '@musinsa/pbo-core'
import request from '@/utils/request'
import { returnInboundQueryKeys } from './useReturnInboundList'

/** BE `ReturnInboundEtc5SettingResponse` — 설정이 없는 화주는 header·inputType 이 null, editable 이 false 다 */
export interface Etc5Setting {
  header: string | null
  editable: boolean
  inputType: 'SELECT' | 'TEXT' | null
}

export const ETC5_KEY = 'ETC5'

const fetchEtc5Setting = async (viewerShipperCode: string): Promise<Etc5Setting> => {
  const res = await request.get<ApiResponseV2<Etc5Setting>>(`/api/v1/return/inbounds/etc5-setting?${new URLSearchParams({ viewerShipperCode })}`)
  return res.data ?? { header: null, editable: false, inputType: null }
}

/** 실패해도 알럿을 띄우지 않는다 — 설정을 못 받으면 칸이 종전처럼 표시 전용으로 남을 뿐 화면 작업은 계속된다 */
export const useReturnInboundEtc5Setting = (viewerShipperCode: string): UseQueryResult<Etc5Setting, Error> =>
  useQuery({
    queryKey: returnInboundQueryKeys.etc5Setting(viewerShipperCode),
    queryFn: () => fetchEtc5Setting(viewerShipperCode),
    enabled: Boolean(viewerShipperCode),
    staleTime: Infinity,
    meta: { noErrorMessage: true },
  })

/**
 * 정불구분 컬럼을 늘 편집 칸으로 열고 화주 설정은 헤더명과 입력 방식에만 쓴다. 설정의 `editable` 은 보지 않는다 —
 * 2.0 은 화주 설정과 관계없이 이 칸을 연다(LOGISPRD-16194, editableColumns.ts 와 같은 결정).
 * 콤보 설정(SELECT)이면 ETC5 콤보 선택, 그 밖(TEXT·설정 없음·아직 못 받음)은 자유 입력이다.
 */
export const applyEtc5Setting = (
  columns: core.CoreColumnType[],
  setting: Etc5Setting | undefined,
  options: { value: string; label: string }[],
): core.CoreColumnType[] =>
  columns.map((column) => {
    if (column.dataIndex !== ETC5_KEY) return column
    const titled = setting?.header ? { ...column, title: setting.header } : column
    if (setting?.inputType === 'SELECT')
      return { ...titled, variant: 'select', variantProps: { ...titled.variantProps, options } } as core.CoreColumnType
    return { ...titled, variant: 'input' } as core.CoreColumnType
  })
